import React, { useRef, useState } from 'react';
import type { ColumnSchema, Condition, DataType, OperatorId } from '@resource-kit/model';
import { Input } from '../primitives/Input';
import { Button } from '../primitives/Button';
import MultiSelect from '../primitives/MultiSelect';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../primitives/Select';
import { getOperatorsFor, isConditionValueValid } from '@resource-kit/controllers';
import {
  Calendar,
  Hash,
  Plus,
  ToggleRight,
  Type as TypeIcon,
  ListChecks,
  Fingerprint,
  Braces,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../primitives/DropdownMenu';
import { DatePicker, DateRangePicker } from '../primitives/DatePicker';
import { useResourceKit } from '@resource-kit/core/react';

type Props = {
  columns: ColumnSchema[];
  value: Condition[];
  onChange: (next: Condition[]) => void;
  /** Rendered inside the bar BEFORE the filter chips (the search chip). */
  leadingSlot?: React.ReactNode;
  /** Rendered inside the bar AFTER the `+` trigger (the search input). */
  trailingSlot?: React.ReactNode;
  /** Clicking the bar's empty area — the trailing slot's own affordance. */
  onEmptyAreaClick?: () => void;
};

// Portal owner marker so QueryBuilder's global click handler ignores Radix portals
const PORTAL_ATTR = { 'data-portal-owner': 'query-builder' } as const;

export default function QueryBuilder({
  columns,
  value,
  onChange,
  leadingSlot,
  trailingSlot,
  onEmptyAreaClick,
}: Props) {
  const { t } = useResourceKit();
  const rootRef = useRef<HTMLDivElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  // Single edit panel
  const [draft, setDraft] = useState<Condition | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  // Close pending state
  const [pendingPanelClose, setPendingPanelClose] = useState(false);

  const remove = (id: string) => onChange(value.filter((v) => v.id !== id));

  const defaultValueForColumn = (col: ColumnSchema | undefined) => {
    if (!col) return '';
    if (col.type === 'boolean') return 'true';
    if (col.type === 'enum' && col.enumValues?.length) return col.enumValues[0].value;
    return '';
  };

  const upsert = (c: Condition) => {
    const idx = value.findIndex((v) => v.id === c.id);
    if (idx >= 0) {
      const next = value.slice();
      next[idx] = c;
      onChange(next);
    } else {
      onChange([...value, c]);
    }
  };

  const handleFieldMenuOpenChange = (open: boolean) => {
    // Opening the menu closes the edit panel, committing a valid draft first
    // — same contract as clicking outside the component — so starting a
    // second filter never silently discards the one being edited.
    if (open && panelOpen) {
      if (validateDraft(draft)) upsert(draft as Condition);
      setPanelOpen(false);
      setDraft(null);
    }
    if (open) setPendingPanelClose(false);
    setFieldMenuOpen(open);
  };

  const selectField = (fieldId: string) => {
    const col = columns.find((c) => c.id === fieldId) || columns[0];
    const ops = getOperatorsFor(col.type, col.operations);
    const cond: Condition = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      field: col.id,
      op: ops[0]?.id || 'eq',
      value: defaultValueForColumn(col),
    };
    setDraft(cond);
    setPanelOpen(true);
    setFieldMenuOpen(false);
  };

  const openEditPanel = (cond: Condition) => {
    setDraft({ ...cond });
    setFieldMenuOpen(false);
    setPanelOpen(true);
    setPendingPanelClose(false);
  };

  // Global click handler
  const validateDraft = (c: Condition | null): boolean => {
    if (!c) return false;
    const col = columns.find((x) => x.id === c.field);
    if (!col) return false;
    const spec = getOperatorsFor(col.type, col.operations).find((o) => o.id === c.op);
    const kind = spec?.valueKind;
    if (!kind) return true;
    // Shared pure validator (operators.ts) — notably blocks the empty
    // `number` input (`Number('') === 0`) from minting a no-op chip.
    return isConditionValueValid(kind, c.value);
  };

  // Close-on-outside-click for the EDIT PANEL only. The field menu is a
  // DropdownMenu now and dismisses itself; leaving it in here would race
  // Base UI's own handler and close the menu the moment it opened.
  const handleGlobalClick = (ev: MouseEvent) => {
    const target = ev.target as Node;
    const insideRoot = !!rootRef.current && rootRef.current.contains(target);
    const insidePortal = (target as Element)?.closest?.('[data-portal-owner]') ? true : false;

    // Click inside component
    if (insideRoot || insidePortal) {
      if (pendingPanelClose) setPendingPanelClose(false);
      return;
    }

    // Close panel logic
    if (panelOpen) {
      if (validateDraft(draft)) {
        upsert(draft as Condition);
        setPanelOpen(false);
        setDraft(null);
        setPendingPanelClose(false);
        return;
      }
      if (!pendingPanelClose) {
        setPendingPanelClose(true);
        return;
      }
      setPanelOpen(false);
      setDraft(null);
      setPendingPanelClose(false);
    }
  };

  // Listeners
  React.useEffect(() => {
    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen, draft, value, pendingPanelClose]);

  const fieldLabel = (fieldId: string) => {
    const raw = columns.find((c) => c.id === fieldId)?.label || fieldId;
    return t(raw, { defaultValue: raw });
  };
  const operatorLabel = (fieldId: string, opId: string) => {
    const column = columns.find((c) => c.id === fieldId);
    if (!column) return opId;
    const list = getOperatorsFor(column.type, column.operations);
    const raw = list.find((o) => o.id === opId)?.label || opId;
    return t(raw, { defaultValue: raw });
  };
  const opLabel = (cond: Condition) => {
    return operatorLabel(cond.field, cond.op);
  };

  const valueLabel = (cond: Condition) => {
    const col = columns.find((c) => c.id === cond.field);
    const raw = Array.isArray(cond.value) ? cond.value : [String(cond.value ?? '')];
    if (col?.type === 'enum' && col.enumValues?.length) {
      return raw
        .map((v) => {
          const opt = col.enumValues!.find((e) => e.value === v);
          return opt?.label ? t(opt.label, { defaultValue: opt.label }) : v;
        })
        .join(', ');
    }
    return raw.join(', ');
  };

  const summary = (cond: Condition) => {
    const col = columns.find((c) => c.id === cond.field);
    if (col?.type === 'boolean') {
      const yes = String(cond.value).toLowerCase() === 'true';
      const label = fieldLabel(cond.field);
      if (cond.op !== 'eq')
        return `${label} ${opLabel(cond)} ${yes ? t('rk.ui.yes') : t('rk.ui.no')}`;
      return `${label}: ${yes ? t('rk.ui.yes') : t('rk.ui.no')}`;
    }
    return `${fieldLabel(cond.field)} ${opLabel(cond)} ${valueLabel(cond)}`.trim();
  };

  return (
    <div
      ref={rootRef}
      // One type scale for everything inside: chips, the panel's Cancel
      // button and the range `–` are bare elements with no size class, and
      // would otherwise inherit the 1rem page default while every
      // Button/Input/Select sibling renders at text-sm.
      className="space-y-2 text-sm"
    >
      {/*
        The bar carries filter chips, the `+` trigger, and (via the slots) the
        list's free-text search — one control instead of the two look-alike
        boxes the toolbar used to stack. Empty-area clicks belong to whatever
        the trailing slot puts there (the search input), NOT to the field menu:
        the menu has its own trigger now.
      */}
      <div
        ref={barRef}
        data-testid="query-bar"
        className="w-full min-h-9 flex items-center flex-wrap gap-2 border border-input rounded-md px-3 py-1 bg-surface-1 focus-within:ring-2 focus-within:ring-ring"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('[data-qb-chip]') || target.closest('button')) return;
          onEmptyAreaClick?.();
        }}
      >
        {leadingSlot}
        {value.map((c) => (
          <span
            key={c.id}
            data-qb-chip
            data-testid="filter-chip"
            className="inline-flex items-center gap-2 bg-primary/10 text-foreground border border-primary/20 px-2 py-0.5 rounded-full cursor-pointer hover:bg-primary/20"
            onClick={(e) => {
              e.stopPropagation();
              openEditPanel(c);
            }}
            title={summary(c)}
          >
            <span className="truncate max-w-[260px]">{summary(c)}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              aria-label={t('rk.query.removeFilter')}
              onClick={(e) => {
                e.stopPropagation();
                remove(c.id);
              }}
            >
              ×
            </button>
          </span>
        ))}
        {/*
          Anchored to the trigger by Base UI, so it follows the button when
          chips push it right or onto a second line. The hand-rolled absolute
          layer it replaces was pinned to the bar's bottom-left corner.
        */}
        <DropdownMenu open={fieldMenuOpen} onOpenChange={handleFieldMenuOpenChange}>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                data-testid="add-filter"
                className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-dashed border-input px-2 py-0.5 text-sm text-muted-foreground hover:border-ring hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('rk.query.addFilter')}
              </button>
            }
          />
          {/*
            No height override: DropdownMenuContent already bounds itself to
            Base UI's `--available-height` and scrolls, so a long field list
            never runs off-screen with its lower items unreachable.
          */}
          <DropdownMenuContent positionerProps={{ side: 'bottom', align: 'start' }}>
            {columns.map((col) => (
              <DropdownMenuItem key={col.id} onClick={() => selectField(col.id)}>
                <FieldTypeIcon type={col.type} />
                <span>{t(col.label, { defaultValue: col.label })}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {trailingSlot}
      </div>

      {/* Editor panel */}
      {panelOpen && draft && (
        <div className="relative">
          <div
            ref={panelRef}
            className="absolute z-30 mt-1 w-full border border-border rounded-md bg-popover text-popover-foreground shadow-md p-3"
            onKeyDown={(e) => {
              if (!draft) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                if (validateDraft(draft)) {
                  upsert(draft);
                  setPanelOpen(false);
                  setDraft(null);
                  setPendingPanelClose(false);
                }
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setPanelOpen(false);
                setDraft(null);
                setPendingPanelClose(false);
              }
            }}
          >
            <div className="flex flex-wrap items-center gap-2">
              {/* Field */}
              <Select
                value={draft.field}
                onValueChange={(nf) => {
                  const ncol = columns.find((c) => c.id === nf) || columns[0];
                  const ops = getOperatorsFor(ncol.type, ncol.operations);
                  setDraft({
                    ...draft,
                    field: nf,
                    op: ops[0]?.id || 'eq',
                    value: defaultValueForColumn(ncol),
                  });
                }}
              >
                <SelectTrigger className="min-w-[12rem] w-auto">
                  <div className="flex items-center gap-2">
                    <FieldTypeIcon
                      type={columns.find((c) => c.id === draft.field)?.type || 'string'}
                    />
                    <span className="truncate">{fieldLabel(draft.field)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent {...PORTAL_ATTR}>
                  {columns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        <FieldTypeIcon type={c.type} />
                        {t(c.label, { defaultValue: c.label })}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Operator */}
              {(() => {
                const col = columns.find((c) => c.id === draft.field) || columns[0];
                const ops = getOperatorsFor(col.type, col.operations);
                if (ops.length <= 1) return null;
                return (
                  <Select
                    value={draft.op}
                    onValueChange={(v) => setDraft({ ...draft, op: v as OperatorId })}
                  >
                    <SelectTrigger className="min-w-[10rem] w-auto">
                      <span className="truncate">{operatorLabel(draft.field, draft.op)}</span>
                    </SelectTrigger>
                    <SelectContent {...PORTAL_ATTR}>
                      {ops.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {t(o.label, { defaultValue: o.label })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}

              {/* Value */}
              {(() => {
                const col = columns.find((c) => c.id === draft.field) || columns[0];
                const ops = getOperatorsFor(col.type, col.operations);
                const spec = ops.find((o) => o.id === draft.op);
                const kind = spec?.valueKind;
                if (!kind || kind === 'none') return <div className="flex-1" />;
                if (kind === 'enum_single' && col.type === 'boolean') {
                  return (
                    <Select
                      value={String(draft.value ?? 'true')}
                      onValueChange={(v) => setDraft({ ...draft, value: v })}
                      // `items` is what Select.Value reads for its label. Without
                      // it the trigger shows the raw value until the popup has
                      // mounted once, so a freshly-opened editor read `true`.
                      items={{ true: t('rk.ui.yes'), false: t('rk.ui.no') }}
                    >
                      <SelectTrigger className="min-w-[8rem] w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent {...PORTAL_ATTR}>
                        <SelectItem value="true">{t('rk.ui.yes')}</SelectItem>
                        <SelectItem value="false">{t('rk.ui.no')}</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }
                if (kind === 'enum_single' && col?.enumValues?.length) {
                  // One source for both the trigger label (`items`) and the
                  // popup rows, so the two can never drift apart.
                  const options = col.enumValues.map((opt) => ({
                    value: opt.value,
                    label: opt.label ? t(opt.label, { defaultValue: opt.label }) : opt.value,
                  }));
                  return (
                    <Select
                      value={String(draft.value || '')}
                      onValueChange={(v) => setDraft({ ...draft, value: v })}
                      items={Object.fromEntries(options.map((o) => [o.value, o.label]))}
                    >
                      <SelectTrigger className="min-w-[10rem] w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent {...PORTAL_ATTR}>
                        {options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }
                if (kind === 'enum_multi') {
                  if (col?.enumValues?.length) {
                    const current: string[] = Array.isArray(draft.value)
                      ? draft.value
                      : String(draft.value || '')
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean);
                    return (
                      <div className="min-w-[12rem]">
                        <MultiSelect
                          fit
                          options={col.enumValues.map((v) => ({
                            value: v.value,
                            label: v.label ? t(v.label, { defaultValue: v.label }) : v.value,
                          }))}
                          value={current}
                          onChange={(arr) => setDraft({ ...draft, value: arr })}
                          placeholder={t('rk.query.selectMultiple')}
                        />
                      </div>
                    );
                  }
                  return (
                    <Input
                      placeholder="v1,v2"
                      value={
                        typeof draft.value === 'string' || typeof draft.value === 'number'
                          ? draft.value
                          : ''
                      }
                      onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                      style={{ width: `${Math.max(10, String(draft.value || '').length || 0)}ch` }}
                      className="min-w-[10rem]"
                    />
                  );
                }
                if (kind === 'number') {
                  return (
                    <Input
                      type="number"
                      value={
                        typeof draft.value === 'string' || typeof draft.value === 'number'
                          ? draft.value
                          : ''
                      }
                      onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                      style={{ width: `${Math.max(8, String(draft.value || '').length || 0)}ch` }}
                      className="min-w-[8rem]"
                    />
                  );
                }
                if (kind === 'date') {
                  // No `withTime`, on a `datetime` column as much as on a
                  // `date` one (#2111). A single-bound filter names a DAY, and
                  // the picker's time row seeds a literal '00:00' into a
                  // date-only choice — which the completion layer then pads to
                  // the end of that MINUTE, so "to Jan 13" returned only
                  // 00:00:00–00:00:59. A bare `YYYY-MM-DD` lets the completion
                  // layer pad to the edge of the day the operator meant.
                  return (
                    <DatePicker
                      value={typeof draft.value === 'string' ? draft.value : ''}
                      onChange={(v) => setDraft({ ...draft, value: v })}
                      className="px-4"
                    />
                  );
                }
                if (kind === 'dateRange') {
                  const arr = Array.isArray(draft.value)
                    ? (draft.value as [string, string])
                    : ['', ''];
                  return (
                    <DateRangePicker
                      value={arr}
                      onChange={(range) => setDraft({ ...draft, value: range })}
                      className="px-4"
                    />
                  );
                }
                if (kind === 'numberRange') {
                  const arr = Array.isArray(draft.value)
                    ? (draft.value as [string, string])
                    : ['', ''];
                  return (
                    <div className="flex items-center gap-2 px-4">
                      <Input
                        type="number"
                        value={arr[0] ?? ''}
                        onChange={(e) =>
                          setDraft({ ...draft, value: [e.target.value, arr[1] ?? ''] })
                        }
                        className="min-w-[6rem]"
                      />
                      <span className="text-muted-foreground">–</span>
                      <Input
                        type="number"
                        value={arr[1] ?? ''}
                        onChange={(e) =>
                          setDraft({ ...draft, value: [arr[0] ?? '', e.target.value] })
                        }
                        className="min-w-[6rem]"
                      />
                    </div>
                  );
                }
                return (
                  <Input
                    value={
                      typeof draft.value === 'string' || typeof draft.value === 'number'
                        ? draft.value
                        : ''
                    }
                    onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                    style={{ width: `${Math.max(10, String(draft.value || '').length || 0)}ch` }}
                    className="min-w-[10rem]"
                  />
                );
              })()}
              {/* Actions inline; push to the right and wrap on small screens */}
              <div className="ml-auto flex items-center gap-2">
                <button
                  className="text-muted-foreground px-2 py-1"
                  onClick={() => {
                    setPanelOpen(false);
                    setDraft(null);
                    setPendingPanelClose(false);
                  }}
                >
                  {t('rk.query.cancel')}
                </button>
                <Button
                  onClick={() => {
                    if (draft && validateDraft(draft)) {
                      upsert(draft);
                      setPanelOpen(false);
                      setDraft(null);
                      setPendingPanelClose(false);
                    }
                  }}
                >
                  {t('rk.ui.search')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldTypeIcon({ type }: { type: DataType }) {
  const common = 'text-muted-foreground/60';
  switch (type) {
    case 'string':
      return <TypeIcon size={16} className={common} />;
    case 'number':
      return <Hash size={16} className={common} />;
    case 'boolean':
      return <ToggleRight size={16} className={common} />;
    case 'enum':
      return <ListChecks size={16} className={common} />;
    case 'date':
    case 'datetime':
      return <Calendar size={16} className={common} />;
    case 'id':
      return <Fingerprint size={16} className={common} />;
    case 'json':
      return <Braces size={16} className={common} />;
    default:
      return <TypeIcon size={16} className={common} />;
  }
}
